import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import type { DocumentType } from "@/ui/organisms/dealTypes";
import { Icon, Button } from "@proda-ai/ui-kit";
import type { IconProp } from "@proda-ai/ui-kit";
import { CoaSelectPrompt, coaStateBlocksContinue } from "./CoaSelectPrompt";
import type { CoaSelectState } from "./CoaSelectPrompt";

export const testIds = {
    documentTypeCard: (value: string) => `ExtractionTypeSelect-card-${value}`,
    nextButton: "ExtractionTypeSelect-next-button",
};

export type ExtractionTypeSelectProps = {
    selectedFileName: string | null | undefined;
    documentType: Extract<
        DocumentType,
        "operating_statement" | "rent_roll"
    > | null;
    setDocumentType: (
        value: Extract<DocumentType, "operating_statement" | "rent_roll">,
    ) => void;
    handleNext: () => void;
    handleCancelClicked: () => void;
    /**
     * The CoA picker state for this upload (see `deriveCoaState` /
     * `useCoaSelection`). Required so every upload surface makes an explicit
     * decision. The prompt renders only when the user has something to do
     * (error → Retry, several permitted CoAs → dropdown); loading and a
     * silently-assigned sole CoA render nothing, with Continue blocked until
     * the checks settle and any required choice is made.
     */
    coaState: CoaSelectState;
};

export const ExtractionTypeSelect = (props: ExtractionTypeSelectProps) => {
    const {
        selectedFileName,
        documentType,
        setDocumentType,
        handleNext,
        handleCancelClicked,
        coaState,
    } = props;

    const coaBlocksContinue = coaStateBlocksContinue(coaState);

    const fileExtension = selectedFileName?.split(".").at(-1)?.toLowerCase();

    const documentTypeOptions = [
        {
            value: "operating_statement" as const,
            label: "Operating Statement",
            icon: "file-spreadsheet" as IconProp,
            disabledFormat: ["csv", "xlsb"],
        },
        {
            value: "rent_roll" as const,
            label: "Rent Roll",
            icon: "scroll" as IconProp,
        },
    ];

    return (
        <Box sx={{ padding: "40px 0" }}>
            <Box
                id="processing-wrapper"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginBottom: "32px",
                    mx: "2rem",
                }}
            >
                <Typography variant="h1" sx={{ marginBottom: "16px" }}>
                    Create an Extraction
                </Typography>

                {selectedFileName != null && selectedFileName.trim() !== "" && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "16px 24px",
                            backgroundColor: "var(--proda-white-l95)",
                            borderRadius: "8px",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography
                            variant="body3"
                            sx={{
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                minWidth: 0,
                                overflow: "hidden",
                            }}
                        >
                            <Icon
                                icon="file-text"
                                size={18}
                                color="var(--proda-black-10)"
                            />
                            <span
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={selectedFileName}
                            >
                                {selectedFileName}
                            </span>
                        </Typography>
                        <Typography
                            variant="body3"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontStyle: "italic",
                                flexShrink: 0,
                            }}
                        >
                            <Icon
                                icon="check-circle"
                                size={12}
                                color="var(--proda-neon-blue)"
                            />
                            {"File Uploaded"}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Typography variant="h2">
                Choose the type of document you want to extract:
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: "16px",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    alignSelf: "stretch",
                    margin: "16px 32px 32px 32px",
                }}
            >
                {documentTypeOptions.map(option => (
                    <Button
                        key={option.value}
                        data-testid={testIds.documentTypeCard(option.value)}
                        onClick={() => setDocumentType(option.value)}
                        pattern="secondary"
                        style={{
                            height: "56px",
                            flex: "1",
                            padding: "16px 32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "8px",
                        }}
                        isActive={documentType === option.value}
                        disabled={option.disabledFormat?.includes(
                            fileExtension ?? "",
                        )}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: "8px",
                            }}
                        >
                            <Icon icon={option.icon} size={16} />
                            <Typography variant="body2">
                                {option.label}
                            </Typography>
                        </Box>
                    </Button>
                ))}
            </Box>

            <CoaSelectPrompt state={coaState} />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "16px",
                }}
            >
                <Button
                    pattern="secondary"
                    size="medium"
                    onClick={handleCancelClicked}
                    style={{
                        width: "120px",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    data-testid={testIds.nextButton}
                    pattern="primary"
                    size="medium"
                    disabled={!documentType || coaBlocksContinue}
                    onClick={handleNext}
                    style={{
                        width: "120px",
                    }}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    );
};
